import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./apiConfig";
import "./results.css";

// Thresholds tuned to the Nomic cosine-similarity distribution where genuine matches cluster ~0.58; easy to recalibrate here.
function matchTier(pct) {
  if (pct >= 58) return { label: "Strong match", tone: "gd-badge--success" };
  if (pct >= 51) return { label: "Good match", tone: "gd-badge--accent" };
  return { label: "Possible match", tone: "" };
}

const FILTER_CHIPS = [
  "All teams",
  "Products",
  "Delivery",
  "Demos",
  "Services",
  "Cross",
  "Design System",
];

const THUMBNAIL_BATCH_SIZE = 20;
// Matches the server's MAX_CONCURRENT_FIGMA_IMAGE_CALLS (figmaThumbnails.js), but
// that cap is process-wide: shared across tabs and users, not per client. When
// it is saturated the server answers with `retryable: true` placeholders
// instead of queueing, and those cards are retried (see below).
const MAX_CONCURRENT_THUMBNAIL_REQUESTS = 3;
// Retry delay grows per attempt (2 s, 4 s, 6 s); after that the card is `failed`.
const THUMBNAIL_RETRY_DELAY_MS = 2000;
const MAX_THUMBNAIL_RETRIES = 3;

function CardThumbnailFallback() {
  return (
    <div aria-hidden="true" className="results-thumb__fallback">
      <svg
        className="gd-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </svg>
    </div>
  );
}

function ThumbnailPreview({
  frameName,
  figmaLink,
  hasExactFrame,
  thumbnailState,
  onImageError,
}) {
  const showImage = thumbnailState?.status === "ready" && thumbnailState.thumbnailUrl;
  const href = figmaLink || "#";

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={
        hasExactFrame
          ? `Open matched frame: ${frameName} in Figma`
          : `Open Figma page for ${frameName}`
      }
      className="results-thumb"
    >
      {showImage ? (
        <div className="results-thumb__frame">
          <img
            src={thumbnailState.thumbnailUrl}
            alt=""
            aria-hidden="true"
            onError={() => onImageError()}
            className="results-thumb__img"
          />
        </div>
      ) : (
        <CardThumbnailFallback />
      )}
    </a>
  );
}

function normalizePercent(score) {
  if (score == null || Number.isNaN(Number(score))) return 0;
  const n = Number(score);
  if (n <= 1 && n >= 0) return Math.round(n * 100);
  return Math.min(100, Math.max(0, Math.round(n)));
}

// Search export (issue #19). Mirrors MAX_QUERY_CHARS in backend/searchExport.js.
const EXPORT_QUERY_MAX_CHARS = 500;
const EXPORT_SLUG_MAX_CHARS = 50;
// Delay before releasing the blob URL so the browser has started the download.
const EXPORT_REVOKE_DELAY_MS = 1000;
const EXPORT_SUCCESS_VISIBLE_MS = 8000;
const EXPORT_GENERIC_ERROR = "We couldn't prepare the download. Please try again.";

// The server owns the file name (and its date); this reads it from
// Content-Disposition. Anything not shaped like our own names is ignored.
function fileNameFromDisposition(header) {
  const match = /filename="([A-Za-z0-9._-]+\.html)"/.exec(header || "");
  return match ? match[1] : null;
}

// Fallback only for when the header is missing (e.g. not exposed by CORS).
function slugifyQuery(query) {
  const slug = String(query || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, EXPORT_SLUG_MAX_CHARS)
    .replace(/-+$/g, "");
  return slug || "results";
}

function exportFileName(query, now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `figma-references-${slugifyQuery(query)}-${date}.html`;
}

// Request text can be pasted prose with line breaks: show and export it as one
// line, and keep the on-screen label short.
const QUERY_LABEL_DISPLAY_MAX_CHARS = 100;

function singleLine(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function shortLabel(text) {
  return text.length > QUERY_LABEL_DISPLAY_MAX_CHARS
    ? `${text.slice(0, QUERY_LABEL_DISPLAY_MAX_CHARS).trimEnd()}\u2026`
    : text;
}

function ResultCompletionStatus({ count, activeQuery }) {
  return (
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="results-visually-hidden"
    >
      Search complete. {count} result{count === 1 ? "" : "s"}
      {activeQuery ? ` for "${activeQuery}".` : "."}
    </span>
  );
}

export default function ResultsGrid({
  results = [],
  activeQuery = "",
  isLoading,
  error,
  searchVersion = 0,
  corpusFreshness = null,
  exportLabel = "",
}) {
  const [teamFilter, setTeamFilter] = useState("All teams");
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportCount, setExportCount] = useState(0);
  const [exportSuccess, setExportSuccess] = useState(null);
  const [exportAnnouncement, setExportAnnouncement] = useState("");
  const exportSuccessTimerRef = useRef(null);
  const exportControllerRef = useRef(null);
  const searchVersionRef = useRef(searchVersion);
  searchVersionRef.current = searchVersion;

  const queryLabel = singleLine(activeQuery);
  const displayQuery = shortLabel(queryLabel);
  // What the Search export is titled; defaults to the query. Pasted Request text
  // is never used as an export title, as exports are handed to colleagues.
  const exportQuery = singleLine(exportLabel) || queryLabel;
  const list = Array.isArray(results) ? results : [];
  const freshnessLabel =
    corpusFreshness && typeof corpusFreshness.label === "string" ? corpusFreshness.label.trim() : "";

  const filtered = useMemo(() => {
    if (teamFilter === "All teams") return list;
    return list.filter((item) => (item?.teamName || "") === teamFilter);
  }, [list, teamFilter]);

  const identityEntries = useMemo(
    () =>
      filtered.map((item, index) => {
        const fileKey = typeof item?.fileKey === "string" ? item.fileKey : "";
        const frameId = typeof item?.frameId === "string" ? item.frameId : "";
        return {
          key: fileKey && frameId ? `${fileKey}\0${frameId}` : `result-${index}`,
          fileKey,
          frameId,
        };
      }),
    [filtered]
  );
  const identityMapRef = useRef(new Map());
  identityMapRef.current = new Map(
    identityEntries
      .filter((identity) => identity.fileKey && identity.frameId)
      .map((identity) => [identity.key, identity])
  );

  const cardNodesRef = useRef(new Map());
  const thumbnailStatusRef = useRef(new Map());
  const [thumbnailStates, setThumbnailStates] = useState(() => new Map());
  const pendingThumbnailKeysRef = useRef(new Set());
  const thumbnailControllersRef = useRef(new Set());
  const activeThumbnailRequestsRef = useRef(0);
  const thumbnailFlushTimerRef = useRef(null);
  const thumbnailGenerationRef = useRef(0);
  const thumbnailRetryAttemptsRef = useRef(new Map());
  const thumbnailRetryTimersRef = useRef(new Set());
  const identitySignature = identityEntries.map((identity) => identity.key).join("\u001f");

  function commitThumbnailStatuses(changes) {
    if (changes.length === 0) return;
    const next = new Map(thumbnailStatusRef.current);
    for (const change of changes) {
      next.set(change.key, {
        status: change.status,
        thumbnailUrl: change.thumbnailUrl || null,
      });
    }
    thumbnailStatusRef.current = next;
    setThumbnailStates(next);
  }

  function clearThumbnailRetries() {
    for (const timer of thumbnailRetryTimersRef.current) clearTimeout(timer);
    thumbnailRetryTimersRef.current.clear();
    thumbnailRetryAttemptsRef.current.clear();
  }

  // Re-queues a capacity miss after a growing delay. Returns false once the
  // key has used up its retries, so the caller marks it failed.
  function scheduleThumbnailRetry(key, generation) {
    const attempts = thumbnailRetryAttemptsRef.current.get(key) || 0;
    if (attempts >= MAX_THUMBNAIL_RETRIES) return false;
    thumbnailRetryAttemptsRef.current.set(key, attempts + 1);
    const timer = setTimeout(() => {
      thumbnailRetryTimersRef.current.delete(timer);
      if (generation !== thumbnailGenerationRef.current) return;
      if (!identityMapRef.current.has(key)) return;
      pendingThumbnailKeysRef.current.add(key);
      scheduleThumbnailDrain();
    }, THUMBNAIL_RETRY_DELAY_MS * (attempts + 1));
    thumbnailRetryTimersRef.current.add(timer);
    return true;
  }

  async function requestThumbnailBatch(items, generation) {
    const controller = new AbortController();
    thumbnailControllersRef.current.add(controller);
    try {
      const response = await apiFetch("/api/search/thumbnails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error(data.message || `Thumbnail request failed (${response.status})`);
      }

      if (generation !== thumbnailGenerationRef.current) return;

      const resultByKey = new Map(
        (Array.isArray(data.results) ? data.results : []).map((result) => [
          `${result?.fileKey || ""}\0${result?.frameId || ""}`,
          result,
        ])
      );
      const changes = [];
      for (const item of items) {
        const key = `${item.fileKey}\0${item.frameId}`;
        const result = resultByKey.get(key);
        const thumbnailUrl = typeof result?.thumbnailUrl === "string" ? result.thumbnailUrl : "";
        // A server capacity miss is transient: the card stays in `loading`
        // and is re-queued, rather than being marked failed for good.
        if (!thumbnailUrl && result?.retryable === true && scheduleThumbnailRetry(key, generation)) {
          continue;
        }
        changes.push({ key, status: thumbnailUrl ? "ready" : "failed", thumbnailUrl });
      }
      commitThumbnailStatuses(changes);
    } catch (requestError) {
      if (requestError?.name === "AbortError" || generation !== thumbnailGenerationRef.current) {
        return;
      }
      commitThumbnailStatuses(
        items.map((item) => ({
          key: `${item.fileKey}\0${item.frameId}`,
          status: "failed",
          thumbnailUrl: null,
        }))
      );
    } finally {
      thumbnailControllersRef.current.delete(controller);
    }
  }

  function drainThumbnailQueue() {
    const generation = thumbnailGenerationRef.current;
    while (
      activeThumbnailRequestsRef.current < MAX_CONCURRENT_THUMBNAIL_REQUESTS &&
      pendingThumbnailKeysRef.current.size > 0
    ) {
      // Each request covers a single file (one Figma image call server-side), so
      // the first thumbnails appear as soon as that file's call returns instead
      // of waiting for every file in the batch. Keys for other files stay pending.
      const items = [];
      let batchFileKey = null;
      for (const key of pendingThumbnailKeysRef.current) {
        const identity = identityMapRef.current.get(key);
        if (!identity) {
          // Unknown identity (e.g. results changed): drop it, as before.
          pendingThumbnailKeysRef.current.delete(key);
          continue;
        }
        if (batchFileKey === null) batchFileKey = identity.fileKey;
        if (identity.fileKey !== batchFileKey) continue;
        pendingThumbnailKeysRef.current.delete(key);
        items.push(identity);
        if (items.length >= THUMBNAIL_BATCH_SIZE) break;
      }
      // Every key was unknown and has been dropped; the loop condition
      // re-checks the (now smaller) queue, so this cannot spin.
      if (items.length === 0) continue;

      activeThumbnailRequestsRef.current += 1;
      void requestThumbnailBatch(items, generation)
        .catch(() => {})
        .finally(() => {
          activeThumbnailRequestsRef.current -= 1;
          drainThumbnailQueue();
        });
    }
  }

  function scheduleThumbnailDrain() {
    if (thumbnailFlushTimerRef.current !== null) return;
    thumbnailFlushTimerRef.current = setTimeout(() => {
      thumbnailFlushTimerRef.current = null;
      drainThumbnailQueue();
    }, 0);
  }

  function enqueueThumbnailKeys(keys) {
    const changes = [];
    for (const key of keys) {
      const identity = identityMapRef.current.get(key);
      if (
        !identity ||
        thumbnailStatusRef.current.has(key) ||
        pendingThumbnailKeysRef.current.has(key)
      ) {
        continue;
      }
      pendingThumbnailKeysRef.current.add(key);
      changes.push({ key, status: "loading", thumbnailUrl: null });
    }
    if (changes.length > 0) {
      commitThumbnailStatuses(changes);
      scheduleThumbnailDrain();
    }
  }

  useEffect(() => {
    thumbnailGenerationRef.current += 1;
    pendingThumbnailKeysRef.current.clear();
    if (thumbnailFlushTimerRef.current !== null) {
      clearTimeout(thumbnailFlushTimerRef.current);
      thumbnailFlushTimerRef.current = null;
    }
    clearThumbnailRetries();
    for (const controller of thumbnailControllersRef.current) controller.abort();
    thumbnailControllersRef.current.clear();
    thumbnailStatusRef.current = new Map();
    setThumbnailStates(new Map());
  }, [searchVersion]);

  // A new search starts clean: an in-flight export for the old results is
  // abandoned and its state reset. Unmount abandons it too.
  useEffect(() => {
    if (exportControllerRef.current) exportControllerRef.current.abort();
    exportControllerRef.current = null;
    clearExportSuccess();
    setIsExporting(false);
    setExportError(null);
    setExportAnnouncement("");
  }, [searchVersion]);

  useEffect(
    () => () => {
      if (exportControllerRef.current) exportControllerRef.current.abort();
      if (exportSuccessTimerRef.current !== null) clearTimeout(exportSuccessTimerRef.current);
    },
    []
  );

  // An export error belongs to the list it was raised for.
  useEffect(() => {
    setExportError(null);
  }, [teamFilter]);

  // Pending retry timers must not fire after the grid is gone.
  useEffect(() => clearThumbnailRetries, []);

  useEffect(() => {
    if (isLoading || error) return undefined;

    const visibleIdentities = identityEntries.filter(
      (identity) => identity.fileKey && identity.frameId
    );
    if (visibleIdentities.length === 0) return undefined;

    if (typeof window !== "undefined" && typeof window.IntersectionObserver === "function") {
      const keyByNode = new WeakMap();
      const observer = new window.IntersectionObserver(
        (entries) => {
          const visibleKeys = entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => {
              const key = keyByNode.get(entry.target);
              observer.unobserve(entry.target);
              return key;
            })
            .filter(Boolean);
          enqueueThumbnailKeys(visibleKeys);
        },
        { rootMargin: "0px" }
      );

      for (const identity of visibleIdentities) {
        const node = cardNodesRef.current.get(identity.key);
        if (node) {
          keyByNode.set(node, identity.key);
          observer.observe(node);
        }
      }
      return () => observer.disconnect();
    }

    // Older browsers use a small viewport check rather than eagerly loading
    // every card; scrolling and resizing discover the next visible cards.
    let animationFrame = null;
    const checkVisibleCards = () => {
      animationFrame = null;
      const visibleKeys = [];
      for (const identity of visibleIdentities) {
        const node = cardNodesRef.current.get(identity.key);
        if (!node) continue;
        const rect = node.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) visibleKeys.push(identity.key);
      }
      enqueueThumbnailKeys(visibleKeys);
    };
    const scheduleVisibleCheck = () => {
      if (animationFrame !== null) return;
      if (typeof window.requestAnimationFrame === "function") {
        animationFrame = window.requestAnimationFrame(checkVisibleCards);
      } else {
        animationFrame = setTimeout(checkVisibleCards, 0);
      }
    };

    checkVisibleCards();
    window.addEventListener("scroll", scheduleVisibleCheck, { passive: true });
    window.addEventListener("resize", scheduleVisibleCheck);
    return () => {
      window.removeEventListener("scroll", scheduleVisibleCheck);
      window.removeEventListener("resize", scheduleVisibleCheck);
      if (animationFrame !== null) {
        if (typeof window.cancelAnimationFrame === "function") {
          window.cancelAnimationFrame(animationFrame);
        } else {
          clearTimeout(animationFrame);
        }
      }
    };
  }, [error, identitySignature, isLoading, searchVersion]);

  useEffect(
    () => () => {
      thumbnailGenerationRef.current += 1;
      pendingThumbnailKeysRef.current.clear();
      if (thumbnailFlushTimerRef.current !== null) {
        clearTimeout(thumbnailFlushTimerRef.current);
        thumbnailFlushTimerRef.current = null;
      }
      for (const controller of thumbnailControllersRef.current) controller.abort();
      thumbnailControllersRef.current.clear();
    },
    []
  );

  const setCardNode = (key, node) => {
    if (node) cardNodesRef.current.set(key, node);
    else cardNodesRef.current.delete(key);
  };

  // Search export: sends only frame identities + scores (never display text) for
  // exactly the cards on screen, in on-screen order; the server fills in the rest.
  function clearExportSuccess() {
    if (exportSuccessTimerRef.current !== null) {
      clearTimeout(exportSuccessTimerRef.current);
      exportSuccessTimerRef.current = null;
    }
    setExportSuccess(null);
  }

  const failExport = (message) => {
    setExportError(message);
    setExportAnnouncement(message);
  };

  const handleDownloadResults = async () => {
    if (isExporting) return;
    const items = filtered
      .filter((item) => typeof item?.fileKey === "string" && item.fileKey && typeof item?.frameId === "string" && item.frameId)
      .map((item) => {
        const exportItem = { fileKey: item.fileKey, frameId: item.frameId };
        if (typeof item.similarityScore === "number" && Number.isFinite(item.similarityScore)) {
          exportItem.score = item.similarityScore;
        }
        return exportItem;
      });
    if (items.length === 0) {
      failExport(EXPORT_GENERIC_ERROR);
      return;
    }
    const controller = new AbortController();
    exportControllerRef.current = controller;
    const clickedVersion = searchVersion;
    const isStale = () => controller.signal.aborted || clickedVersion !== searchVersionRef.current;
    setIsExporting(true);
    setExportCount(items.length);
    setExportError(null);
    clearExportSuccess();
    setExportAnnouncement("Preparing download…");
    try {
      const res = await apiFetch("/api/search/export", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: exportQuery.slice(0, EXPORT_QUERY_MAX_CHARS), items }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const failure = new Error(data.message || `Could not build the download (${res.status})`);
        // Only the rate-limit message is meant for the designer; the rest is logged.
        failure.userFacing = res.status === 429 && Boolean(data.message);
        throw failure;
      }
      const fileName =
        fileNameFromDisposition(res.headers.get("Content-Disposition")) || exportFileName(exportQuery);
      const blob = await res.blob();
      if (isStale()) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), EXPORT_REVOKE_DELAY_MS);
      setExportSuccess(fileName);
      setExportAnnouncement(`Download started: ${fileName}`);
      exportSuccessTimerRef.current = setTimeout(() => {
        exportSuccessTimerRef.current = null;
        setExportSuccess(null);
      }, EXPORT_SUCCESS_VISIBLE_MS);
    } catch (err) {
      if (isStale()) return;
      console.error("Search export failed:", err);
      failExport(err?.userFacing ? err.message : EXPORT_GENERIC_ERROR);
    } finally {
      if (!isStale()) {
        setIsExporting(false);
        exportControllerRef.current = null;
      }
    }
  };

  if (error) {
    return (
      <div className="gd-notice gd-notice--danger" role="alert">
        <span className="gd-notice__icon">
          <svg
            className="gd-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" x2="12" y1="8" y2="12" />
            <line x1="12" x2="12.01" y1="16" y2="16" />
          </svg>
        </span>
        <div className="gd-notice__body">{error}</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="results-grid">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="gd-card results-card">
            <div className="results-skel results-skel--thumb" />
            <div className="results-card__body">
              <div className="results-skel results-skel--title" />
              <div className="results-skel results-skel--line" />
              <div className="results-skel results-skel--line-short" />
              <div className="results-skel results-skel--badge" />
              <div className="results-skel results-skel--button" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="results gd-stack">
      <ResultCompletionStatus count={filtered.length} activeQuery={displayQuery} />
      <span role="status" aria-live="polite" aria-atomic="true" className="results-visually-hidden">
        {exportAnnouncement}
      </span>
      <div className="gd-row gd-row--wrap results-bar">
        <p className="body-strong results-count">
          {filtered.length} result{filtered.length === 1 ? "" : "s"}
          {displayQuery ? (
            <span className="results-count__query">
              {" "}
              for &ldquo;{displayQuery}&rdquo;
            </span>
          ) : null}
        </p>
        {freshnessLabel || filtered.length > 0 ? (
          <div className="results-actions">
            {freshnessLabel ? (
              <span
                title="When this tool last checked Figma for new and changed designs"
                className="small gd-muted"
              >
                {freshnessLabel}
              </span>
            ) : null}
            {freshnessLabel && filtered.length > 0 ? (
              <span aria-hidden="true" className="results-actions__divider" />
            ) : null}
            {filtered.length > 0 ? (
              <div className="results-actions__column">
                <button
                  type="button"
                  onClick={handleDownloadResults}
                  aria-disabled={isExporting}
                  aria-busy={isExporting}
                  className="gd-btn"
                >
                  {isExporting ? (
                    <svg
                      className="gd-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg
                      className="gd-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <path d="M7 10l5 5 5-5" />
                      <path d="M12 15V3" />
                    </svg>
                  )}
                  {isExporting ? "Preparing…" : "Download results"}
                </button>
                {isExporting ? (
                  <span className="small gd-muted results-export-note">
                    Preparing {exportCount} thumbnail{exportCount === 1 ? "" : "s"}. This takes about 20
                    seconds. Keep this tab open.
                  </span>
                ) : null}
                {!isExporting && exportSuccess ? (
                  <span className="small gd-muted results-export-note">
                    Downloaded {exportSuccess}. Send it to a colleague; it opens in any browser.
                  </span>
                ) : null}
                {exportError ? (
                  <span className="gd-error results-export-note">
                    <svg
                      className="gd-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4" />
                      <path d="M12 16h.01" />
                    </svg>
                    {exportError}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="gd-row gd-row--wrap results-filters" role="group" aria-label="Filter by team">
        {FILTER_CHIPS.map((chip) => {
          const active = teamFilter === chip;
          return (
            <button
              key={chip}
              type="button"
              className="gd-chip"
              aria-pressed={active}
              onClick={() => setTeamFilter(chip)}
            >
              {chip}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="gd-empty results-empty">
          <div className="gd-empty__mark" aria-hidden="true" />
          <h3 className="gd-empty__title">
            {teamFilter === "All teams" ? "No results found" : `No results for "${teamFilter}"`}
          </h3>
          <p className="gd-empty__text">
            {teamFilter === "All teams"
              ? "Try a different search query."
              : `Try "All teams" or another team.`}
          </p>
        </div>
      ) : (
        <div className="results-grid">
          {filtered.map((item, index) => {
            const identity = identityEntries[index];
            const frameName = item?.frameName?.trim() || "Untitled frame";
            const pageName = item?.pageName?.trim() || "—";
            const fileName = item?.fileName ?? "—";
            const teamName = item?.teamName ?? "";
            const figmaLink = typeof item?.figmaLink === "string" ? item.figmaLink : "";
            const hasScore = item?.similarityScore != null && !Number.isNaN(Number(item.similarityScore));
            const pct = hasScore ? normalizePercent(item.similarityScore) : 0;
            const tier = hasScore ? matchTier(pct) : null;
            const thumbnailState = thumbnailStates.get(identity.key);

            return (
              <article
                key={`${identity.key}-${index}`}
                ref={(node) => setCardNode(identity.key, node)}
                className="gd-card results-card"
              >
                <ThumbnailPreview
                  frameName={frameName}
                  figmaLink={figmaLink}
                  hasExactFrame={Boolean(identity.fileKey && identity.frameId)}
                  thumbnailState={thumbnailState}
                  onImageError={() =>
                    commitThumbnailStatuses([
                      { key: identity.key, status: "failed", thumbnailUrl: null },
                    ])
                  }
                />

                <div className="results-card__body">
                  <h3 title={frameName} className="body-strong results-card__title">
                    {frameName}
                  </h3>

                  <p className="small gd-muted results-card__file">{fileName}</p>

                  <p className="small gd-muted results-card__page">
                    <svg
                      className="gd-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <path d="M14 2v6h6" />
                    </svg>
                    <span>{pageName}</span>
                  </p>

                  <span className="gd-badge results-card__team" title={teamName}>
                    {teamName || "Team"}
                  </span>

                  {hasScore && tier && (
                    <div
                      aria-label={`${tier.label} — ${pct}% similarity`}
                      className="results-card__score"
                    >
                      <span aria-hidden="true" className={`gd-badge ${tier.tone}`.trim()}>
                        {tier.label}
                      </span>
                      <span aria-hidden="true" className="small gd-muted">
                        {pct}%
                      </span>
                    </div>
                  )}

                  <a
                    href={figmaLink || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={
                      identity.fileKey && identity.frameId
                        ? `Open matched frame: ${frameName} in Figma`
                        : `Open Figma page for ${frameName}`
                    }
                    className="gd-btn gd-btn--block results-card__link"
                  >
                    Open in Figma
                    <svg
                      className="gd-icon gd-icon--directional"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M7 7h10v10" />
                      <path d="M7 17 17 7" />
                    </svg>
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
